import { IUserSession } from "common/Session";
import { copyValueOrObject, JsonCopy } from "./Misc";
import SessionUtils from "./SessionUtils";
import { PropertyTypes, PublishTypes } from "../../globals";
import { defaultEditorPreferences, IEditorPreferences } from "../../interfaces/Preferences";
import WorkspaceManager from "../WorkspaceManager";
import { PreviewAspects } from "../../components/uisystem/PreviewAspect";
import { FactoryPresetLayouts } from "../../components/workspace/WorkspaceLayouts";
import { FactoryDefaultKeyBindings, KeyCodes, ModifierKeyCodes } from "../../interfaces/Input";
import { FeatureFlagNames } from "../../interfaces/enums";
import store, { StoreActions } from "../../state/store";
import Engine from "../Engine";
import ARTracker from "../ARTracker";
import { IUserSettings, SortDirections, Subset } from "../../interfaces/Misc";
import { ScenarioSortAttributes } from "../../interfaces/Worklink";
import { defaultDevice } from "../../interfaces/VisageUI";

export default class PreferencesUtils
{
	static isOldFormat(preferences: IPreferences): boolean
	{
		if (!preferences || typeof preferences !== 'object') return false;
		
		// Check if any section has the old format structure (sectionLabel + items array)
		for (const sectionKey of Object.keys(preferences)) 
		{
			const section = preferences[sectionKey];
			if (section && typeof section === 'object') 
			{
				for (const subsectionKey of Object.keys(section)) 
				{
					const subsection = section[subsectionKey];
					// Check if it matches IPreferenceSection structure
					if (subsection?.sectionLabel && Array.isArray(subsection?.items)) 
					{
						return true;
					}
				}
			}
		}
		
		return false;
	}

	static convertToNewFormat(prefs: any): IEditorPreferences
	{
		const output = { ...defaultEditorPreferences };
		
		// invalid preferences - return the defaults
		if(!prefs || typeof prefs !== "object")
		{
			return output;
		}
		
		// not old, return the preferences
		if(!this.isOldFormat(prefs))
		{
			return prefs;
		}

		// update properties by id
		for(const section of Object.values(prefs as IPreferences))
		{
			for(const subsection of Object.values(section))
			{
				for(const preference of subsection.items)
				{
					if(preference.id in output)
					{
						output[preference.id] = copyValueOrObject(preference.value ?? preference.defaultValue);
					}
				}
			}
		}

		// special handling for timebar colors
		const timebarColorSubsection = (prefs as IPreferences)["timeline"]?.["timebarDisplay"];
		if(timebarColorSubsection)
		{
			// each one starts with bgColor_ and then the actual timebar effector type
			for(const timebarColor of timebarColorSubsection.items)
			{
				const effectorType = timebarColor.id.replace("bgColor_", "");
				if(effectorType in output.timebarColors)
				{
					output.timebarColors[effectorType] = timebarColor.value ?? timebarColor.defaultValue;
				}
			}
		}

		return output;
	}

	static loadWlcSettings(sessionInfo: IUserSession)
	{
		const defaultSettings: IUserSettings = {
			workspace: {
				desktopWindows: {},
				layout: WorkspaceManager.getCustomLayoutByName("Default Workspace"),
				customLayouts: {
					factoryPresetLayouts: FactoryPresetLayouts,
					userPresetLayouts: []
				},
			},
			uiPreview: {
				scale: 100,
				aspect: PreviewAspects.ASPECT_16_BY_9,
			},
			preferences: JsonCopy(defaultEditorPreferences),
			worklink: {
				scenarioList: {
					pageSize: 25,
					sortingAttribute: ScenarioSortAttributes.Name,
					sortingMethod: SortDirections.Ascending,
				},
			},
			visageUI: {
				displayDevice: defaultDevice(),
				editorSettings: {
					automaticMode: true,
					canvasScale: 1,
					fitCanvas: true,
					showViewport: false,
				},
			},
			view: {
				spritesVisible: true,
				trackersVisible: true,
				gridVisible: true,
				uiVisible: true,
			},
			publishSettings: {
				bypassCPS: false,
				encrypt: false,
				openInInteractiveMode: false,
				publishType: PublishTypes.persistent_per_user,
			},
		};

		// load from server or default
		let wlcSettings: IUserSettings = defaultSettings;
		if (sessionInfo.wlc_settings)
		{
			try
			{
				wlcSettings = JSON.parse(sessionInfo!.wlc_settings.replace("SceneHierarchy", "StepHierarchy"));
			}
			catch (e)
			{
				console.error("Failed to load WLC settings from session info: ", e);
			}
		}

		WorkspaceManager.load(wlcSettings.workspace);

		let keybindings = {...FactoryDefaultKeyBindings};
		if (wlcSettings.preferences?.keybindings)
		{
			try
			{
				keybindings = JsonCopy(wlcSettings.preferences.keybindings);
				if(SessionUtils.isFeatureFlagEnabled(FeatureFlagNames.partsCatalog) && keybindings.ObjectProperties?.keycode === KeyCodes.P && keybindings.ObjectProperties?.modifiers.includes(ModifierKeyCodes.ALT))
				{
					keybindings.ObjectProperties = {...FactoryDefaultKeyBindings.ObjectProperties};
					keybindings.PartsCatalogEditor = {...FactoryDefaultKeyBindings.PartsCatalogEditor};
				}
			}
			catch (e)
			{
				console.error("Failed to load keybinding data from WLC settings: ", e);
			}
		}

		store.dispatch(StoreActions.preferences.setEditorPreferences({
			...PreferencesUtils.convertToNewFormat(wlcSettings.preferences),
			keybindings: JsonCopy(keybindings),
		}));

		store.dispatch(StoreActions.ui.setUIPreviewScale({ value: wlcSettings.uiPreview?.scale ?? 100 }));
		store.dispatch(StoreActions.ui.setUIPreviewAspectRatio({ value: wlcSettings.uiPreview?.aspect ?? PreviewAspects.ASPECT_16_BY_9 }));

		if(wlcSettings.visageUI)
		{
			store.dispatch(StoreActions.visage.setCurrentDisplayDevice(wlcSettings.visageUI.displayDevice));
			store.dispatch(StoreActions.visage.setEditorSettings(wlcSettings.visageUI.editorSettings));
		}

		if(wlcSettings.view)
		{
			if(wlcSettings.view.uiVisible !== undefined)
			{
				store.dispatch(StoreActions.ui.setUIPreviewVisible({ value: wlcSettings.view.uiVisible }));
			}
			if(wlcSettings.view.spritesVisible !== undefined)
			{
				store.dispatch(StoreActions.workspace.setSpritesVisible(wlcSettings.view.spritesVisible));
				Engine.getInstance().setSpritesVisibility(wlcSettings.view.spritesVisible);
			}
			if(wlcSettings.view.trackersVisible !== undefined)
			{
				store.dispatch(StoreActions.workspace.setTrackersVisible(wlcSettings.view.trackersVisible));
				ARTracker.setTrackersVisibility(wlcSettings.view.trackersVisible);
			}
			if(wlcSettings.view.gridVisible !== undefined)
			{
				store.dispatch(StoreActions.workspace.setGridVisible(wlcSettings.view.gridVisible));
				Engine.getInstance().getGrid().visible = wlcSettings.view.gridVisible;
			}
		}

		if(wlcSettings.publishSettings)
		{
			store.dispatch(StoreActions.application.setPublishSettings(wlcSettings.publishSettings));		
		}

		if(wlcSettings.worklink)
		{
			store.dispatch(StoreActions.worklink.setSettings(wlcSettings.worklink));
		}
	}
}

export interface IPreferenceItem
{
	id: string;
	value: any;
	defaultValue: any;
	label?: string;
	type?: PropertyTypes;
	min?: number;
	max?: number;
	step?: number;
	unitScaling?: boolean;
	tooltipKey?: string;
	labelClass?: string;
	optionList?: Record<string, any>;
	disabled?: boolean;
	postfixLabel?: string;
}

export interface IPreferenceSection
{
	sectionLabel: string;
	items: IPreferenceItem[];
	hidden?: boolean;
}

export interface IPreferences
{
	[section: string]: {
		[subsection: string]: IPreferenceSection;
	};
}
